import { ActionMenu } from '@components/ActionMenu';
import { ConfirmDialog } from '@components/ConfirmDialog';
import { useCurrentProject } from '@modules/projects/utils/useCurrentProject';
import { Delete, Description, Receipt } from '@mui/icons-material';
import { Divider, ListItemIcon, MenuItem, Tooltip } from '@mui/material';
import { useNotification } from '@utils/useNotification';
import { useQueryInvalidator } from '@utils/useQueryInvalidator';
import { useDeleteCostItemMutation, useBudgetItemsQuery, useGetProjectBudgetQuery, useExpensesQuery } from 'gql/index';
import React from 'react';
import { useIntl } from 'react-intl';
import { useBudgetTableContext } from './BudgetItemTable/context/useBudgetTableContext';

interface Props {
  costItemId: number;
  hasEntries: boolean;
}

export const CostItemActions: React.FC<Props> = ({ costItemId, hasEntries }) => {
  const { formatMessage } = useIntl();
  const { projectId, canManageBudgetAndExpenses } = useCurrentProject();
  const { notifySuccess } = useNotification();
  const invalidateQuery = useQueryInvalidator();
  const { setEntryDialog } = useBudgetTableContext();

  const [isDeleting, setIsDeleting] = React.useState(false);

  const { mutate: deleteCostItem, isPending } = useDeleteCostItemMutation({
    onSuccess: () => {
      notifySuccess(formatMessage({ id: 'Cost item deleted successfully' }));
      invalidateQuery(useBudgetItemsQuery, { projectId });
      invalidateQuery(useExpensesQuery, { projectId });
      invalidateQuery(useGetProjectBudgetQuery, { id: projectId });
    }
  });

  if (!canManageBudgetAndExpenses) {
    return null;
  }

  const onDelete = () => {
    deleteCostItem({
      input: {
        id: costItemId,
        projectId,
      }
    });
  };

  const deleteDisabledMessage = formatMessage({ id: 'Cost items with entries cannot be deleted.' });

  return <>
    <ActionMenu>
      <MenuItem onClick={() => setEntryDialog({ open: true, costItemId, type: 'expense' })}>
        <ListItemIcon>
          <Receipt />
        </ListItemIcon>
        {formatMessage({ id: 'Add expense' })}
      </MenuItem>
      <MenuItem onClick={() => setEntryDialog({ open: true, costItemId, type: 'quote' })}>
        <ListItemIcon>
          <Description />
        </ListItemIcon>
        {formatMessage({ id: 'Add quote' })}
      </MenuItem>
      <Divider />
      <Tooltip title={hasEntries ? deleteDisabledMessage : ''}>
        <span>
          <MenuItem onClick={() => setIsDeleting(true)} disabled={hasEntries}>
            <ListItemIcon sx={{ color: 'error.main' }}>
              <Delete color='error' />
            </ListItemIcon>
            {formatMessage({ id: 'Delete' })}
          </MenuItem>
        </span>
      </Tooltip>
    </ActionMenu>

    <ConfirmDialog
      open={isDeleting}
      onCancel={() => setIsDeleting(false)}
      onConfirm={onDelete}
      title={formatMessage({ id: 'Delete cost item' })}
      confirmColor='error'
      confirmText={formatMessage({ id: 'Delete' })}
      content={formatMessage({ id: 'Are you sure you want to delete this cost item?' })}
      loading={isPending}
    />
  </>;
};

